import type { JSX } from 'react';

/**
 * Inline SVG country flags. Picked over an npm dep / CDN to keep the
 * storefront self-contained — no runtime fetch, nothing to block on
 * a corp network. Each flag is 1KB or less of inline markup.
 *
 * Flag artwork is geometric public-domain heraldry (stripes / stars /
 * solid fields), simplified to render correctly at the 14–20px sizes
 * we use in the language switcher and drawer rows. The US flag's
 * 50-star canton is omitted because the stars are sub-pixel at this
 * size and would just smear into the blue field; the 13 stripes +
 * canton block carry the recognition.
 *
 * To add a new flag: add a branch to `flagFor()` and render the
 * appropriate SVG. Component is keyed on the lowercase ISO 3166-1
 * alpha-2 code so it lines up with our existing language code map
 * (en → us, zh → cn, etc.).
 */

interface CountryFlagProps {
	/**
	 * Lowercase ISO 3166-1 alpha-2 country code (e.g. 'us', 'cn').
	 * Falls back to a neutral placeholder for codes we don't render.
	 */
	code: string;
	/** Tailwind / className override for sizing + positioning. */
	className?: string;
	/** Accessible label; defaults to the country code in caps. */
	title?: string;
}

// Single 5-point star path centered at (0,0), outer radius 1, point up.
// Reused by `<use>` from the China flag's 5 stars to keep the SVG tight.
const STAR_PATH =
	'M 0,-1 L 0.225,-0.309 L 0.951,-0.309 L 0.363,0.118 L 0.588,0.809 L 0,0.382 L -0.588,0.809 L -0.363,0.118 L -0.951,-0.309 L -0.225,-0.309 Z';

function UsFlag({
	className,
	title,
}: Omit<CountryFlagProps, 'code'>): JSX.Element {
	// 13 stripes (7 red, 6 white). Blue canton covers top 7 stripes
	// (7 × 10/13 ≈ 5.385) and 40% width (7.6 of 19).
	const stripeH = 10 / 13;
	return (
		<svg
			viewBox="0 0 19 10"
			className={className}
			role="img"
			aria-label={title ?? 'US'}
		>
			<rect width="19" height="10" fill="#B22234" />
			<g fill="#fff">
				{[1, 3, 5, 7, 9, 11].map((i) => (
					<rect key={i} y={i * stripeH} width="19" height={stripeH} />
				))}
			</g>
			<rect width="7.6" height={7 * stripeH} fill="#3C3B6E" />
		</svg>
	);
}

function CnFlag({
	className,
	title,
}: Omit<CountryFlagProps, 'code'>): JSX.Element {
	// 30×20 canvas. Big star centered at (5,5) outer-radius ≈3. Four
	// small stars around it at the canonical PRC offsets, scaled to
	// outer-radius 1. Yellow on red (#FFDE00 / #DE2910 — official).
	return (
		<svg
			viewBox="0 0 30 20"
			className={className}
			role="img"
			aria-label={title ?? 'CN'}
		>
			<rect width="30" height="20" fill="#DE2910" />
			<g fill="#FFDE00">
				<g transform="translate(5,5) scale(3)">
					<path d={STAR_PATH} />
				</g>
				<g transform="translate(10,2) scale(1) rotate(-23.04)">
					<path d={STAR_PATH} />
				</g>
				<g transform="translate(12,4) scale(1) rotate(-45.87)">
					<path d={STAR_PATH} />
				</g>
				<g transform="translate(12,7) scale(1) rotate(-69.95)">
					<path d={STAR_PATH} />
				</g>
				<g transform="translate(10,9) scale(1) rotate(-20.66)">
					<path d={STAR_PATH} />
				</g>
			</g>
		</svg>
	);
}

// Trigram bar rows (local frame: bars horizontal, stacked on y).
// Official proportions put each bar at 1/12 of the taegeuk diameter —
// sub-pixel at our render sizes — so bars are slightly thickened and
// the broken-bar gap widened to keep the corner marks legible.
const TRIGRAM_ROWS = [-1.5, 0, 1.5];

function Trigram({
	pattern,
	transform,
}: {
	/** Per bar, top to bottom: true = solid, false = broken (two segments). */
	pattern: [boolean, boolean, boolean];
	transform: string;
}): JSX.Element {
	return (
		<g transform={transform} fill="#000">
			{pattern.map((solid, i) =>
				solid ? (
					<rect
						key={i}
						x="-2.5"
						y={TRIGRAM_ROWS[i] - 0.5}
						width="5"
						height="1"
					/>
				) : (
					<g key={i}>
						<rect x="-2.5" y={TRIGRAM_ROWS[i] - 0.5} width="2" height="1" />
						<rect x="0.5" y={TRIGRAM_ROWS[i] - 0.5} width="2" height="1" />
					</g>
				),
			)}
		</g>
	);
}

function KrFlag({
	className,
	title,
}: Omit<CountryFlagProps, 'code'>): JSX.Element {
	// 30×20 canvas. Taegeuk centered with radius 5 (diameter = half the
	// flag height, per the official construction) and rotated 33.69° so
	// the red/blue division runs along the flag's diagonal. Blue is the
	// exact comma shape (half-disc + two half-radius arcs) painted over
	// a red disc. The four corner trigrams sit on the diagonals with
	// their bars perpendicular to the diagonal they sit on. Official
	// colors: #CD2E3A red / #0047A0 blue.
	return (
		<svg
			viewBox="0 0 30 20"
			className={className}
			role="img"
			aria-label={title ?? 'KR'}
		>
			<rect width="30" height="20" fill="#fff" />
			<g transform="translate(15,10) rotate(33.69)">
				<circle r="5" fill="#CD2E3A" />
				<path
					d="M -5 0 A 5 5 0 1 0 5 0 A 2.5 2.5 0 1 0 0 0 A 2.5 2.5 0 1 1 -5 0 Z"
					fill="#0047A0"
				/>
			</g>
			{/* Geon ☰ — upper hoist */}
			<Trigram
				pattern={[true, true, true]}
				transform="translate(7.84,5.23) rotate(-56.31)"
			/>
			{/* Gam ☵ — upper fly */}
			<Trigram
				pattern={[false, true, false]}
				transform="translate(22.16,5.23) rotate(56.31)"
			/>
			{/* Ri ☲ — lower hoist */}
			<Trigram
				pattern={[true, false, true]}
				transform="translate(7.84,14.77) rotate(56.31)"
			/>
			{/* Gon ☷ — lower fly */}
			<Trigram
				pattern={[false, false, false]}
				transform="translate(22.16,14.77) rotate(-56.31)"
			/>
		</svg>
	);
}

/**
 * Map a language code (or country code) to a country flag. The
 * portal uses 2-char language codes (`en`, `zh`, `ko`); we route
 * those to the obvious flags below (en → us, zh → cn, ko → kr).
 * Unknown codes fall back to a neutral placeholder so we don't
 * crash on new languages.
 */
export function CountryFlag({
	code,
	className,
	title,
}: CountryFlagProps): JSX.Element {
	const normalized = code.toLowerCase();
	switch (normalized) {
		case 'en':
		case 'us':
			return <UsFlag className={className} title={title ?? 'US'} />;
		case 'zh':
		case 'cn':
			return <CnFlag className={className} title={title ?? 'CN'} />;
		case 'ko':
		case 'kr':
			return <KrFlag className={className} title={title ?? 'KR'} />;
		default:
			// Unknown — render a neutral grey rounded rect so the row
			// still has a visual anchor + the label still reads.
			return (
				<svg
					viewBox="0 0 19 10"
					className={className}
					role="img"
					aria-label={title ?? normalized.toUpperCase()}
				>
					<rect width="19" height="10" fill="#9CA3AF" rx="1" />
				</svg>
			);
	}
}
